import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';

import { Header } from 'dibs-elements/exports/Header';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import { Link } from 'dibs-elements/exports/Link';
import { trackEvent } from 'dibs-tracking';
import { FormattedMessage } from 'dibs-react-intl';

import { useViewMore } from '../hooks/viewMore/useViewMore';
import { viewMore, viewLess } from '../sbMessages';
import { Visibility } from '../../components/global/Visibility';

import dibsCss from 'dibs-css';
import classnames from 'classnames';
import styles from './SbSharedRelatedCreatorBuyPages.scss';

import { type SbSharedRelatedCreatorBuyPages_creatorRootPage$key } from './__generated__/SbSharedRelatedCreatorBuyPages_creatorRootPage.graphql';

type Props = {
    creatorRootPage: SbSharedRelatedCreatorBuyPages_creatorRootPage$key;
    isMobile: boolean;
};

const INITIAL_COUNT = 4;
const MOBILE_REVEAL_COUNT = 4;
const RESP_REVEAL_COUNT = 4;

export const SbSharedRelatedCreatorBuyPages: FC<Props> = ({
    creatorRootPage: creatorRootPageRef,
    isMobile,
}) => {
    const creatorRootPage = useFragment(
        graphql`
            fragment SbSharedRelatedCreatorBuyPages_creatorRootPage on CreatorRootPage {
                relatedCreatorBuyPages(relatedBuyPagesCount: $relatedBuyPagesCount) {
                    relatedCreatorBuyPage {
                        creatorBuyPageLinkData {
                            ...SeoLink_linkData
                            text
                        }
                        creatorBuyPageFirstItem {
                            photos(limit: 1) {
                                smallPath
                            }
                        }
                    }
                }
            }
        `,
        creatorRootPageRef
    );
    const relatedBuyPages = (creatorRootPage?.relatedCreatorBuyPages || []).map(pages => {
        const relatedCreatorBuyPage = pages?.relatedCreatorBuyPage;
        return {
            relatedCreatorBuyPage: {
                creatorBuyPageLinkData: relatedCreatorBuyPage?.creatorBuyPageLinkData,
                creatorBuyPageFirstItem: {
                    photos: (relatedCreatorBuyPage?.creatorBuyPageFirstItem?.photos || []).map(
                        photo => ({
                            smallPath: photo?.smallPath || '',
                        })
                    ),
                },
            },
        };
    });
    const relatedBuyPagesCount = relatedBuyPages.length;

    const { viewMoreEl, dispatch, visibleCount } = useViewMore<HTMLAnchorElement>({
        totalCount: relatedBuyPagesCount,
        revealCount: MOBILE_REVEAL_COUNT,
        initialCount: INITIAL_COUNT,
    });

    let relatedBuyPagesDisplay = relatedBuyPages;
    if (isMobile) {
        relatedBuyPagesDisplay = relatedBuyPages.slice(0, visibleCount);
    } else if (relatedBuyPagesCount < RESP_REVEAL_COUNT) {
        relatedBuyPagesDisplay = [];
    }

    let linkRefProp = {};
    if (viewMoreEl.current) {
        linkRefProp = { linkRef: viewMoreEl };
    }

    return relatedBuyPagesDisplay.length > 0 ? (
        <div className={classnames(dibsCss.flex, dibsCss.flexCol, !isMobile && dibsCss.pMedium)}>
            <HeadingLevel>
                {Heading => (
                    <div className={isMobile ? dibsCss.mbXsmall : dibsCss.mbSmall}>
                        <Header
                            title={
                                <FormattedMessage
                                    id="SbSharedRelatedCreatorBuyPages.title"
                                    defaultMessage="Popular Searches"
                                />
                            }
                            htmlElementType={Heading}
                        />
                    </div>
                )}
            </HeadingLevel>
            <Visibility
                onContentVisible={() =>
                    trackEvent({
                        category: 'promo interaction',
                        action: 'creator popular searches module impression',
                        label: 'none',
                    })
                }
            />
            <div
                className={classnames(
                    dibsCss.flex,
                    dibsCss.justifyAround,
                    isMobile
                        ? dibsCss.flexWrap
                        : [dibsCss.bgMoonstone, dibsCss.ptLarge, dibsCss.pxMedium, dibsCss.pbMedium]
                )}
            >
                {relatedBuyPagesDisplay.map(({ relatedCreatorBuyPage }, index) => {
                    const photo = relatedCreatorBuyPage?.creatorBuyPageFirstItem?.photos?.[0];
                    const linkData = relatedCreatorBuyPage?.creatorBuyPageLinkData;
                    return (
                        linkData && (
                            <SeoLink
                                key={`creator-buy-page-${index}`}
                                dataTn={`popular-searches-link-${index}`}
                                linkData={linkData}
                                className={classnames(
                                    styles.popularSearchLink,
                                    dibsCss.sassyFontBody,
                                    dibsCss.flex,
                                    dibsCss.itemsCenter,
                                    isMobile
                                        ? [dibsCss.basisFull, dibsCss.myXsmall]
                                        : dibsCss.flexCol
                                )}
                                onClick={() =>
                                    trackEvent({
                                        category: 'promo interaction',
                                        action: 'creator popular searches module click',
                                        label: 'none',
                                    })
                                }
                            >
                                <div
                                    className={classnames(
                                        styles.imageWrapper,
                                        dibsCss.flex,
                                        dibsCss.itemsCenter,
                                        dibsCss.overflowHidden,
                                        dibsCss.pXsmall,
                                        dibsCss.bgWhite,
                                        dibsCss.roundedFull
                                    )}
                                >
                                    <img
                                        className={dibsCss.maxWFull}
                                        src={photo?.smallPath}
                                        alt=""
                                    />
                                </div>
                                <div
                                    className={classnames(
                                        isMobile
                                            ? dibsCss.sassyFontBody
                                            : [
                                                  dibsCss.sassyFontBodySizeSmallTypeHeavy,
                                                  dibsCss.mtSmall,
                                              ]
                                    )}
                                >
                                    {linkData?.text}
                                </div>
                            </SeoLink>
                        )
                    );
                })}
            </div>
            {isMobile && relatedBuyPagesCount > MOBILE_REVEAL_COUNT && (
                <div className={classnames(dibsCss.mtXsmall, dibsCss.mbSmall)}>
                    <Link {...linkRefProp} onClick={dispatch} className={dibsCss.sassyFontBody}>
                        {visibleCount === relatedBuyPagesCount ? viewLess : viewMore}
                    </Link>
                </div>
            )}
        </div>
    ) : null;
};
